import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getTableFilter, getDatasetTags as getTags } from "@/helpers/apis";
import {
  useDatatableContext,
  FilterConfig,
} from "@/components/tables/contexts/DatatableContextProvider";
import AddFiltersButton from "@/components/tables/AddFiltersButton";
import { FilterType, TableHeaderTypeBase } from "@/components/tables/types";
import { FilterApplicableFor } from "@/components/tags/types";
import {
  DebouncedInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
} from "@altimateai/lego";
import { capitalize } from "lodash";
import { getIconByDatastoreType } from "./DatastoreImage";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useDatasetsFilterOptions } from "./useDatasetsFilterOptions";
import { useAppState } from "@/modules/app/useAppContext";

const GET_TABLE_FILTERS = "GET-TABLE-FILTERS";
const GET_TAGS = "GET-TAGS";

/**
 * New horizontal filter bar for Datasets page.
 * Horizontal filter bar for the Datasets page.
 *
 * Cascading behavior (in More Filters panel):
 *   Instance selection → filters database options to that instance's databases
 *   Database selection → filters schema options to that database's schemas
 *   Without a parent selection, all options are shown.
 */
interface DatasetsNewFiltersProps {
  onSearchChange: (value: string) => void;
}

const DatasetsNewFilters = ({ onSearchChange }: DatasetsNewFiltersProps) => {
  const { datastores } = useAppState();
  const { registerFilters } = useDatatableContext();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();

  const dsType = searchParams.get("datasource_type") || "snowflake";

  const { data: tableFilters } = useQuery({
    queryKey: [GET_TABLE_FILTERS],
    queryFn: getTableFilter,
  });

  const { data: tagOptions } = useQuery({
    queryKey: [GET_TAGS],
    queryFn: getTags,
  });

  const { getInstanceOptions, getDatabaseOptions, getSchemaOptions } = useDatasetsFilterOptions({
    tableFilters,
    dsType,
    datastores,
  });

  const instanceOptions = useMemo(() => getInstanceOptions(), [getInstanceOptions]);

  const tagFilterOptions = useMemo(() => {
    if (!tagOptions?.column_badges) return [];
    return tagOptions.column_badges.map((tag: string) => ({ label: tag, value: tag }));
  }, [tagOptions]);

  // Register filters keyed by stable searchKey only. Options live in `filterHeaders`
  // (used by the sheet) — keeping them out of registered config avoids re-registering
  // when async option data resolves, which would reset URL-synced filter values to "".
  useEffect(() => {
    const filters: Record<string, FilterConfig> = {
      datasources: {
        label: "Instance",
        filterType: FilterType.DROPDOWN,
        searchKey: "datasources",
      },
      databases: { label: "Database", filterType: FilterType.DROPDOWN, searchKey: "databases" },
      schemas: { label: "Schema", filterType: FilterType.DROPDOWN, searchKey: "schemas" },
      insight: {
        label: "Insight",
        filterType: FilterType.DROPDOWN,
        searchKey: "insight",
      },
    };
    registerFilters(filters);
  }, [registerFilters]);

  const noop = () => null;
  const filterHeaders = useMemo((): TableHeaderTypeBase[] => {
    return [
      {
        id: 1,
        uid: "datasources",
        label: "Instance",
        render: noop,
        filter: {
          filterType: FilterType.DROPDOWN,
          searchKey: "datasources",
          options: instanceOptions,
        },
      },
      {
        id: 2,
        uid: "databases",
        label: "Database",
        render: noop,
        filter: {
          filterType: FilterType.DROPDOWN,
          searchKey: "databases",
          getOptions: getDatabaseOptions,
        },
      },
      {
        id: 3,
        uid: "schemas",
        label: "Schema",
        render: noop,
        filter: {
          filterType: FilterType.DROPDOWN,
          searchKey: "schemas",
          getOptions: getSchemaOptions,
        },
      },
      {
        id: 4,
        uid: "insight",
        label: "Insight",
        render: noop,
        filter: {
          filterType: FilterType.DROPDOWN,
          searchKey: "insight",
          options: tagFilterOptions,
        },
      },
    ];
  }, [instanceOptions, getDatabaseOptions, getSchemaOptions, tagFilterOptions]);

  const handleDatasourceTypeChange = (value: string) => {
    updateSearchParamsByKey({
      datasource_type: value,
      datasources: undefined,
      databases: undefined,
      schemas: undefined,
    });
  };

  return (
    <Stack gap="md" align="center" className="al-w-full">
      <Select value={dsType} onValueChange={handleDatasourceTypeChange}>
        <SelectTrigger
          className="al-bg-white al-rounded al-border al-border-gray-200 al-w-auto al-whitespace-nowrap"
          aria-label="Select connection type"
        >
          <SelectValue placeholder="Connection Type">
            <Stack align="center" gap="xs">
              <span className="al-text-gray-500 al-text-sm al-font-semibold al-whitespace-nowrap">
                Connection Type:
              </span>
              <span className="al-text-brand al-text-sm al-font-semibold">
                {capitalize(dsType)}
              </span>
            </Stack>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {["s3", "postgres", "snowflake"].map(type => (
            <SelectItem key={type} value={type}>
              <Stack align="center" gap="sm">
                {getIconByDatastoreType(type, "al-h-5 al-w-5")}
                <span>{capitalize(type)}</span>
              </Stack>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <DebouncedInput
        debounceMs={1000}
        placeholder="Search for Datasets and Columns"
        onChange={onSearchChange}
        aria-label="Search for Datasets and Columns"
        className="al-flex-1"
      />

      <div id="dataset-saved-filters-container" />
      <div id="dataset-filter-container" />

      <AddFiltersButton
        portals={{
          filter: "dataset-filter-container",
          savedFilters: "dataset-saved-filters-container",
        }}
        headers={filterHeaders}
        applicableFor={FilterApplicableFor.DATASETS}
      />
    </Stack>
  );
};

export default DatasetsNewFilters;
